import {
  fetchCurrentSessionUser,
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
  storeSessionUser,
  updateCurrentUserEmail as updateCurrentUserEmailSession,
} from '../../../app/services/session.service';
import { getOwnStaffProfile, upsertOwnStaffProfile } from '../../staff/services/staff.service';

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return { ok, data, error, message };
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();
  return token ? { ...extraHeaders, Authorization: `Bearer ${token}` } : extraHeaders;
}

async function mapAuthFailure(response, fallbackMessage) {
  const payload = await parseJson(response);
  if (isSessionRefreshRequiredPayload(payload)) {
    return buildMutationResult({ ok: false, error: 'SETTINGS_SESSION_REFRESH_REQUIRED', message: payload?.message || fallbackMessage });
  }
  if (isSessionExpiredPayload(payload)) {
    return buildMutationResult({ ok: false, error: 'SETTINGS_SESSION_EXPIRED', message: payload?.message || fallbackMessage });
  }
  return buildMutationResult({ ok: false, error: 'SETTINGS_AUTH_REQUIRED', message: payload?.message || fallbackMessage });
}

export async function loadProfileSettings() {
  try {
    const [authUser, staffResult] = await Promise.all([
      fetchCurrentSessionUser(),
      getOwnStaffProfile(),
    ]);
    return buildMutationResult({
      ok: true,
      data: {
        authUser,
        staffProfile: staffResult.ok ? staffResult.data.profile : null,
        staffProfileMissing: staffResult.error === 'STAFF_PROFILE_MISSING',
        staffError: staffResult.ok || staffResult.error === 'STAFF_PROFILE_MISSING' ? '' : staffResult.message,
      },
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: error?.code || 'SETTINGS_PROFILE_LOAD_FAILED',
      message: error?.message || 'Impostazioni profilo non disponibili.',
    });
  }
}

export async function updateAuthProfile({ fullName }) {
  try {
    const response = await fetch(getAuthApiUrl('/api/auth/me/profile'), {
      method: 'PATCH',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ fullName }),
    });
    if (response.status === 401 || response.status === 403) {
      return await mapAuthFailure(response, 'Sessione non autorizzata per aggiornare il profilo.');
    }
    const payload = await parseJson(response);
    if (!response.ok) {
      return buildMutationResult({ ok: false, error: 'SETTINGS_AUTH_PROFILE_FAILED', message: payload?.message || 'Profilo account non aggiornato.' });
    }
    storeSessionUser(payload);
    return buildMutationResult({ ok: true, data: { user: payload }, message: 'Profilo account aggiornato.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'SETTINGS_AUTH_PROFILE_UNAVAILABLE', message: 'Auth service non disponibile.' });
  }
}

export async function updateProfile(profile) {
  const authResult = await updateAuthProfile({ fullName: profile.fullName });
  if (!authResult.ok) {
    return authResult;
  }

  const staffResult = await upsertOwnStaffProfile(profile);
  if (!staffResult.ok) {
    return staffResult;
  }

  return buildMutationResult({
    ok: true,
    data: {
      user: authResult.data.user,
      staffProfile: staffResult.data.profile,
    },
    message: 'Profilo aggiornato.',
  });
}

export async function updateEmail(email) {
  try {
    const session = await updateCurrentUserEmailSession(email);
    return buildMutationResult({
      ok: true,
      data: {
        user: session.user,
        token: session.token,
        expiresAt: session.expiresAt,
      },
      message: 'Email aggiornata.',
    });
  } catch (error) {
    return buildMutationResult({
      ok: false,
      error: error?.code || 'SETTINGS_EMAIL_UPDATE_FAILED',
      message: error?.message || 'Email non aggiornata.',
    });
  }
}

export function updatePassword() {
  return true;
}

export function updatePhone() {
  return buildMutationResult({
    ok: false,
    error: 'PHONE_PROFILE_REQUIRED',
    message: 'Aggiorna il telefono dal profilo personale completo.',
  });
}
